import { create } from "zustand";

export interface SeedCandidate {
  /** S2 venue (facets 1b). Absent on candidates cached before it shipped. */
  journal?: string | null;
  paper_id: string;
  title: string;
  authors: string[];
  year: number | null;
  abstract: string | null;
  citation_count: number;
  doi: string | null;
  overlap_score: number;
  reason: string;
  /** "search" (S2 search on the concept's terms) or "llm" (recalled title resolved on S2). */
  source?: string;
  /** "llm" (selector picked it) or "fallback" (citation order because the selector failed). */
  selection?: string;
  /** Search source(s) that found it (spec 2026-09-26). Missing on candidates
   *  cached before OpenAlex shipped — treat as ["s2"]. */
  sources?: string[];
  /** Selector's 1-based rank against the question (2026-10-30); absent on older caches. */
  rank?: number | null;
  /** "core" = about the concept's own subject, "context" = wider-field background. */
  tier?: "core" | "context" | null;
  /** Selector's 0-100 judgement of how directly the paper answers the question. */
  relevance?: number | null;
}

export interface SeedDiscoveryProgress {
  current: number;
  total: number;
}

interface SeedDiscoveryState {
  isRunning: boolean;
  isCompleted: boolean;
  error: string | null;
  jobId: string | null;
  phase:
    | "idle"
    | "searching"
    | "suggesting"
    | "resolving"
    | "selecting"
    | "analyzing"
    | "complete"
    | "error";
  resolveProgress: SeedDiscoveryProgress | null;
  analyzeProgress: SeedDiscoveryProgress | null;
  candidates: SeedCandidate[] | null;
  /** Bumped by newRun() and reset(); a discovery run whose id is stale must not
   *  write (setup resets on leaving ③, and Curate pops a dialog on candidates). */
  runId: number;
  /** Who started the run (facets 1c-3): Curate's pill + dialog react only to
   *  "concept"; the Add-angle sheet reads the store only for its own targetKey. */
  owner: DiscoveryOwner;
  /** useSeedDiscovery.discoveryTargetKey of the run's target. */
  targetKey: string;

  /** Claim the store for a new run: bumps runId and resets every run field. */
  newRun: (owner?: DiscoveryOwner, targetKey?: string) => number;
  start: (jobId: string) => void;
  setPhase: (phase: SeedDiscoveryState["phase"], progress?: SeedDiscoveryProgress) => void;
  setComplete: (candidates: SeedCandidate[]) => void;
  setError: (message: string) => void;
  dismissPill: () => void;
  reset: () => void;
  /** reset() only if the current run's targetKey is `key` (sheet close). */
  resetIfOwned: (key: string) => void;
}

export type DiscoveryOwner = "concept" | "angle";

const IDLE = {
  isRunning: false,
  isCompleted: false,
  error: null,
  jobId: null,
  phase: "idle" as const,
  resolveProgress: null,
  analyzeProgress: null,
  candidates: null,
};

export const useSeedDiscoveryStore = create<SeedDiscoveryState>((set, get) => ({
  isRunning: false,
  isCompleted: false,
  error: null,
  jobId: null,
  phase: "idle",
  resolveProgress: null,
  analyzeProgress: null,
  candidates: null,
  runId: 0,
  owner: "concept",
  targetKey: "concept",

  newRun: (owner = "concept", targetKey = "concept") => {
    const runId = get().runId + 1;
    set({ ...IDLE, runId, owner, targetKey });
    return runId;
  },

  start: (jobId) =>
    set({
      isRunning: true,
      isCompleted: false,
      error: null,
      jobId,
      phase: "searching",
      resolveProgress: null,
      analyzeProgress: null,
      candidates: null,
    }),

  setPhase: (phase, progress) =>
    set({
      phase,
      ...(phase === "resolving" && progress ? { resolveProgress: progress } : {}),
      ...(phase === "analyzing" && progress ? { analyzeProgress: progress } : {}),
    }),

  setComplete: (candidates) =>
    set({
      isRunning: false,
      isCompleted: true,
      // Clear any prior failed-run state so the cached/saved-candidates path
      // (which calls setComplete without a fresh start()) doesn't leave the
      // operation bar showing a stale error or progress.
      error: null,
      jobId: null,
      resolveProgress: null,
      analyzeProgress: null,
      phase: "complete",
      candidates,
    }),

  setError: (message) =>
    set({
      isRunning: false,
      error: message,
      phase: "error",
    }),

  dismissPill: () =>
    set({
      isCompleted: false,
      error: null,
    }),

  reset: () =>
    set((s) => ({ ...IDLE, runId: s.runId + 1, owner: "concept", targetKey: "concept" })),

  resetIfOwned: (key) => {
    if (get().targetKey === key) get().reset();
  },
}));
